import { Component, Input, Output, EventEmitter, forwardRef, OnDestroy, OnInit, HostListener, ElementRef, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { url_servicios } from 'src/app/config/config';
import { ApiV2Response, ListResponse } from '../../../core/models';

export interface LazySearchSelectOption {
  value: unknown;
  label: string;
  secondaryLabel?: string;
  data?: Record<string, unknown>;
}

export type LazySearchSelectStatusLabelMode = 'inactive' | 'non-active' | 'none';

@Component({
  selector: 'app-lazy-search-select',
  templateUrl: './lazy-search-select.component.html',
  styleUrls: ['./lazy-search-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LazySearchSelectComponent),
      multi: true
    }
  ]
})
export class LazySearchSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder: string = 'Search...';
  @Input() noOptionsText: string = 'No options found';
  @Input() loadingText: string = 'Loading...';
  @Input() pageSize: number = 15;
  @Input() clearable: boolean = true;
  @Input() url!: string; // API endpoint for fetching options
  @Input() optionValue: string = 'id'; // Field to use as value
  @Input() optionLabel!: string; // Field(s) to use as label (comma-separated)
  @Input() optionSecondaryLabel?: string; // Optional field(s) to show under the main label
  @Input() multiple: boolean = false; // Single or multiple selection mode
  @Input() optionFilter?: (item: Record<string, unknown>) => boolean;
  @Input() additionalParams: Record<string, string | number | boolean> = {};
  @Input() statusLabelMode: LazySearchSelectStatusLabelMode = 'inactive';
  @Input() statusLabelResolver?: (item: Record<string, unknown>) => unknown;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectionChange = new EventEmitter<{ value: any }>();
  @Output() clearSelections = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchControl = new FormControl('');
  isDropdownOpen = false;

  readonly destroy$ = new Subject<void>();

  options: LazySearchSelectOption[] = [];
  selectedItems: LazySearchSelectOption[] = [];

  isLoading = false;
  hasMore = true;
  currentPage = 1;
  currentSearchTerm = '';

  highlightedIndex = -1;

  // ControlValueAccessor implementation
  onChange: (value: unknown | unknown[] | null) => void = () => {};
  onTouched: () => void = () => {};
  disabled = false;
  value: unknown | unknown[] | null;

  private _optionLabels: string[] = [];
  private _optionSecondaryLabels: string[] = [];

  private http = inject(HttpClient);
  private elementRef = inject(ElementRef);

  ngOnInit(): void {
    this._optionLabels = this.parseLabelFields(this.optionLabel);
    this._optionSecondaryLabels = this.parseLabelFields(this.optionSecondaryLabel);

    // Setup search with debouncing
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.currentSearchTerm = searchTerm || '';

        // In single selection mode, clear selection when user starts typing
        if (!this.multiple && this.hasSelectedItems && searchTerm) {
          this.selectedItems = [];
          this.value = null;
        }

        // Open dropdown when user starts typing
        if (!this.isDropdownOpen && searchTerm) {
          this.isDropdownOpen = true;
        }
        this.fetchOptions(this.currentSearchTerm, 1);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor methods
  writeValue(value: unknown): void {
    if (value === null || value === undefined) {
      this.selectedItems = [];
      this.value = this.multiple ? [] : null;
      return;
    }

    // Handle multiple selection
    if (this.multiple) {
      const values = Array.isArray(value) ? value : [value];
      this.loadSelectedItems(values);
      this.value = values;
    } else {
      // Single selection
      this.loadSelectedItems([value]);
      this.value = value;
    }
  }

  registerOnChange(fn: (value: unknown | unknown[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  // Load selected items by fetching from API
  private async loadSelectedItems(values: unknown[]): Promise<void> {
    if (!values || values.length === 0) {
      this.selectedItems = [];
      return;
    }

    try {
      const promises = values.map((v) => this.fetchById(v));
      const selectedItems = await Promise.all(promises);
      this.selectedItems = selectedItems.filter(
        (item): item is LazySearchSelectOption => item !== null,
      );
    } catch (error) {
      console.error('Error loading selected items:', error);
      // Fallback: show values as labels
      this.selectedItems = values.map((v) => ({
        value: v,
        label: `ID: ${v}`
      }));
    }
  }

  // Fetch options from API
  private fetchOptions(search: string = '', page: number = 1): void {
    if (!this.url) return;

    this.isLoading = true;

    if (page === 1) {
      this.currentPage = 1;
      this.highlightedIndex = -1;
    }

    let params = new HttpParams()
      .set('search', search)
      .set('page', page.toString())
      .set('per_page', this.pageSize.toString());

    Object.entries(this.additionalParams).forEach(([key, value]) => {
      params = params.set(key, String(value));
    });

    this.http.get<ApiV2Response<ListResponse<Record<string, unknown>>>>(`${url_servicios}${this.url}`, { params })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const newOptions = res.data.data
            .filter((item) => this.shouldIncludeOption(item))
            .map((item) => this.transformData(item));

          if (page === 1) {
            this.options = newOptions;
            this.setDefaultHighlight();
          } else {
            this.options = [...this.options, ...newOptions];
          }

          const currentPage = res.data.current_page ?? page;
          this.currentPage = currentPage;
          this.hasMore = currentPage < res.data.last_page;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching options:', error);
          this.isLoading = false;
          if (page > 1) {
            this.currentPage--;
          }
        }
      });
  }

  // Fetch single item by ID
  private async fetchById(id: unknown): Promise<LazySearchSelectOption | null> {
    try {
      const baseUrl = this.url.split('?')[0];
      const res = await this.http
        .get<{ data: Record<string, unknown> }>(`${url_servicios}${baseUrl}/${id}`)
        .toPromise();
      const data = res?.data ?? {};
      return this.shouldIncludeOption(data) ? this.transformData(data) : null;
    } catch (error) {
      console.error(`Error fetching item by ID ${id}:`, error);
      return { value: id, label: `ID: ${id}` };
    }
  }

  // Transform API response to option format
  private transformData(item: Record<string, unknown>): LazySearchSelectOption {
    const labels = this.buildLabel(item, this._optionLabels);
    const secondaryLabel = this.buildLabel(item, this._optionSecondaryLabels);
    const optionValue = item[this.optionValue];
    const label = this.appendStatusLabel(labels || `ID: ${String(optionValue ?? '')}`, item);

    return {
      value: optionValue,
      label,
      ...(secondaryLabel ? { secondaryLabel } : {}),
      data: item,
    };
  }

  private shouldIncludeOption(item: Record<string, unknown>): boolean {
    return this.optionFilter ? this.optionFilter(item) : true;
  }

  private parseLabelFields(fields: string | undefined): string[] {
    return fields
      ? fields.split(',').map((field) => field.trim()).filter(Boolean)
      : [];
  }

  private buildLabel(item: Record<string, unknown>, fields: string[]): string {
    return fields
      .map((field) => (typeof item[field] === 'string' || typeof item[field] === 'number' ? String(item[field]) : ''))
      .join(' ')
      .trim();
  }

  private appendStatusLabel(label: string, item: Record<string, unknown>): string {
    const status = this.getNormalizedStatus(item);

    if (!status || this.statusLabelMode === 'none') {
      return label;
    }

    if (this.statusLabelMode === 'inactive') {
      return status === 'inactive' ? `${label} - ${status}` : label;
    }

    return status !== 'active' ? `${label} - ${status}` : label;
  }

  private getNormalizedStatus(item: Record<string, unknown>): string {
    const value = this.statusLabelResolver
      ? this.statusLabelResolver(item)
      : item['status'];

    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  // UI Methods
  onInputFocus(): void {
    this.isDropdownOpen = true;
    this.setDefaultHighlight();
    if (this.options.length === 0) {
      this.fetchOptions('', 1);
    }
  }

  focusInput(): void {
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }

  onInputClick(): void {
    if (!this.disabled) {
      this.isDropdownOpen = true;
      this.setDefaultHighlight();
      if (this.options.length === 0) {
        this.fetchOptions('', 1);
      }
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.highlightedIndex = -1;
    this.searchControl.setValue('', { emitEvent: false });
  }

  selectOption(option: LazySearchSelectOption): void {
    if (this.multiple) {
      // Multiple selection mode
      const exists = this.selectedItems.find(item => item.value === option.value);
      if (!exists) {
        this.selectedItems = [...this.selectedItems, option];
        this.value = this.selectedItems.map(item => item.value);
        this.onChange(this.value);
        this.selectionChange.emit({ value: this.value });
      }

      this.currentSearchTerm = '';
      this.setDefaultHighlight();
    } else {
      // Single selection mode
      this.selectedItems = [option];
      this.value = option.value;
      this.onChange(this.value);
      this.selectionChange.emit({ value: this.value });
      this.closeDropdown();
    }

    this.onTouched();
    this.searchControl.setValue('', { emitEvent: false });
  }

  removeItem(item: LazySearchSelectOption, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.multiple) {
      this.selectedItems = this.selectedItems.filter(i => i.value !== item.value);
      this.value = this.selectedItems.map(i => i.value);
    } else {
      this.selectedItems = [];
      this.value = null;
    }

    this.onChange(this.value);
    this.selectionChange.emit({ value: this.value });
    this.onTouched();
  }

  clearAll(): void {
    this.selectedItems = [];
    this.value = this.multiple ? [] : null;
    this.searchControl.setValue('', { emitEvent: false });
    this.onChange(this.value);
    this.selectionChange.emit({ value: this.value });
    this.clearSelections.emit();
    this.onTouched();
  }

  // Keyboard navigation
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isDropdownOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        this.isDropdownOpen = true;
        this.setDefaultHighlight();
        if (this.options.length === 0) {
          this.fetchOptions('', 1);
        }
        event.preventDefault();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.options.length - 1);
        this.scrollToHighlighted();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        this.scrollToHighlighted();
        break;

      case 'Enter':
        event.preventDefault();
        {
          const indexToSelect = this.highlightedIndex >= 0
            ? this.highlightedIndex
            : (this.options.length > 0 ? 0 : -1);
          if (indexToSelect >= 0 && indexToSelect < this.options.length) {
            this.highlightedIndex = indexToSelect;
            this.selectOption(this.options[indexToSelect]);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;

      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  private scrollToHighlighted(): void {
    // Scroll highlighted item into view
    setTimeout(() => {
      const dropdownElement = this.elementRef.nativeElement.querySelector('.dropdown-menu');
      const highlightedElement = this.elementRef.nativeElement.querySelector('.dropdown-item.highlighted');

      if (dropdownElement && highlightedElement) {
        const dropdownRect = dropdownElement.getBoundingClientRect();
        const highlightedRect = highlightedElement.getBoundingClientRect();

        if (highlightedRect.bottom > dropdownRect.bottom) {
          highlightedElement.scrollIntoView({ block: 'nearest' });
        } else if (highlightedRect.top < dropdownRect.top) {
          highlightedElement.scrollIntoView({ block: 'nearest' });
        }
      }
    });
  }

  onOptionMouseEnter(index: number): void {
    this.highlightedIndex = index;
  }

  loadMore(): void {
    if (this.hasMore && !this.isLoading) {
      this.currentPage++;
      this.fetchOptions(this.currentSearchTerm, this.currentPage);
    }
  }

  onDropdownScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const threshold = 50; // pixels from bottom
    const position = target.scrollTop + target.clientHeight;
    const height = target.scrollHeight;

    if (position > height - threshold) {
      this.loadMore();
    }
  }

  // Click outside to close dropdown
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  get hasSelectedItems(): boolean {
    return this.selectedItems.length > 0;
  }

  private setDefaultHighlight(): void {
    if (this.options.length > 0 && this.highlightedIndex === -1) {
      this.highlightedIndex = 0;
    }
  }

  get displayPlaceholder(): string {
    if (this.hasSelectedItems && !this.multiple) {
      return '';
    }
    return this.placeholder;
  }

  trackByValue(index: number, item: LazySearchSelectOption): unknown {
    void index;
    return item.value;
  }

  isOptionSelected(option: LazySearchSelectOption): boolean {
    return this.selectedItems.some(item => item.value === option.value);
  }
}
